import type { DashboardLayout, Row, RowLayout, Slot, SlotSize, WidgetInstance } from './types'
import { SLOT_COUNT_BY_LAYOUT, SLOT_SIZE_BY_LAYOUT, isSlotAtLeast } from './types'

export type SlotContext = {
  row: Row
  rowIndex: number
  slot: Slot
  slotIndex: number
  size: SlotSize
}

export function findSlotContext(layout: DashboardLayout, slotId: string): SlotContext | undefined {
  for (const [rowIndex, row] of layout.rows.entries()) {
    for (const [slotIndex, slot] of row.slots.entries()) {
      if (slot.slotId === slotId) {
        return { row, rowIndex, slot, slotIndex, size: SLOT_SIZE_BY_LAYOUT[row.layout] }
      }
    }
  }
  return undefined
}

/** Widget ids currently placed anywhere in the layout (one entry per instance). */
export function getPlacedWidgetIds(layout: DashboardLayout): Set<string> {
  const ids = new Set<string>()
  for (const row of layout.rows) {
    for (const slot of row.slots) {
      if (slot.widget) ids.add(slot.widget.widgetId)
    }
  }
  return ids
}

/**
 * Returns `true` when moving the widget at `fromSlotId` to `toSlotId` is allowed
 * given each widget's declared `minSize`. If the destination is empty, only the
 * source widget's size compatibility is checked. If both slots are occupied,
 * both widgets must fit the other's target slot size for the swap to be allowed.
 *
 * `getMinSize` is injected (rather than imported directly from a registry) to
 * keep this module pure and unit-testable without rendering React components.
 */
export function isSwapAllowed(
  layout: DashboardLayout,
  fromSlotId: string,
  toSlotId: string,
  getMinSize: (widgetId: string) => SlotSize | undefined
): boolean {
  if (fromSlotId === toSlotId) return false
  const from = findSlotContext(layout, fromSlotId)
  const to = findSlotContext(layout, toSlotId)
  if (!from || !to) return false

  if (from.slot.widget && !isSlotAtLeast(to.size, getMinSize(from.slot.widget.widgetId))) return false
  if (to.slot.widget && !isSlotAtLeast(from.size, getMinSize(to.slot.widget.widgetId))) return false
  return true
}

export function applyMoveOrSwap(
  layout: DashboardLayout,
  fromSlotId: string,
  toSlotId: string
): DashboardLayout {
  if (fromSlotId === toSlotId) return layout
  const from = findSlotContext(layout, fromSlotId)
  const to = findSlotContext(layout, toSlotId)
  if (!from || !to) return layout

  return {
    ...layout,
    rows: layout.rows.map((row, rowIndex) => {
      if (rowIndex !== from.rowIndex && rowIndex !== to.rowIndex) return row
      return {
        ...row,
        slots: row.slots.map((slot, slotIndex) => {
          if (rowIndex === from.rowIndex && slotIndex === from.slotIndex) {
            return { ...slot, widget: to.slot.widget }
          }
          if (rowIndex === to.rowIndex && slotIndex === to.slotIndex) {
            return { ...slot, widget: from.slot.widget }
          }
          return slot
        })
      }
    })
  }
}

/**
 * Sets a specific slot's widget. Pass `null` to clear; pass a `WidgetInstance`
 * to place. Size-compatibility is enforced at the hook layer (so this helper
 * stays pure and unit-testable without the registry).
 */
export function applySetWidget(
  layout: DashboardLayout,
  slotId: string,
  widget: WidgetInstance | null
): DashboardLayout {
  const ctx = findSlotContext(layout, slotId)
  if (!ctx) return layout
  return {
    ...layout,
    rows: layout.rows.map((row, rowIndex) => {
      if (rowIndex !== ctx.rowIndex) return row
      return {
        ...row,
        slots: row.slots.map((slot, slotIndex) => {
          if (slotIndex !== ctx.slotIndex) return slot
          return { ...slot, widget }
        })
      }
    })
  }
}

/**
 * Replaces the `settings` value on the widget instance in `slotId`. No-op if
 * the slot is empty or does not exist. Reference-equal on no-op so callers can
 * rely on it (e.g. memoised selectors).
 */
export function applySetWidgetSettings(
  layout: DashboardLayout,
  slotId: string,
  settings: unknown
): DashboardLayout {
  const ctx = findSlotContext(layout, slotId)
  if (!ctx || !ctx.slot.widget) return layout
  return {
    ...layout,
    rows: layout.rows.map((row, rowIndex) => {
      if (rowIndex !== ctx.rowIndex) return row
      return {
        ...row,
        slots: row.slots.map((slot, slotIndex) => {
          if (slotIndex !== ctx.slotIndex) return slot
          if (!slot.widget) return slot
          return { ...slot, widget: { ...slot.widget, settings } }
        })
      }
    })
  }
}

export function applyRemoveWidget(layout: DashboardLayout, slotId: string): DashboardLayout {
  const ctx = findSlotContext(layout, slotId)
  if (!ctx) return layout
  return {
    ...layout,
    rows: layout.rows.map((row, rowIndex) => {
      if (rowIndex !== ctx.rowIndex) return row
      return {
        ...row,
        slots: row.slots.map((slot, slotIndex) => {
          if (slotIndex !== ctx.slotIndex) return slot
          return { ...slot, widget: null }
        })
      }
    })
  }
}

/**
 * Changes a row's layout (e.g. `thirds` → `halves`) and reshapes its slots:
 *  - Shrinking row counts trim trailing slots; any widget in a trimmed slot is
 *    returned in `displaced`.
 *  - Growing row counts pad with new empty slots, drawing fresh slot ids from
 *    `newSlotIds` so callers can keep id generation in the dispatch layer
 *    (keeps reducers pure).
 *  - Kept slots whose widget no longer fits the new slot width are emptied and
 *    that widget is also returned in `displaced`.
 *
 * Returns the next layout and the list of displaced widget instances. The
 * caller decides whether to ask the user for confirmation (when `displaced` is
 * non-empty) before dispatching.
 */
export function applyChangeRowLayout(
  layout: DashboardLayout,
  rowId: string,
  nextLayout: RowLayout,
  newSlotIds: string[],
  getMinSize: (widgetId: string) => SlotSize | undefined
): { layout: DashboardLayout; displaced: WidgetInstance[] } {
  const rowIndex = layout.rows.findIndex((r) => r.rowId === rowId)
  if (rowIndex < 0) return { layout, displaced: [] }
  const row = layout.rows[rowIndex]!
  if (row.layout === nextLayout) return { layout, displaced: [] }

  const nextCount = SLOT_COUNT_BY_LAYOUT[nextLayout]
  const nextSize = SLOT_SIZE_BY_LAYOUT[nextLayout]
  const displaced: WidgetInstance[] = []

  const kept = row.slots.slice(0, nextCount).map((slot) => {
    if (slot.widget && !isSlotAtLeast(nextSize, getMinSize(slot.widget.widgetId))) {
      displaced.push(slot.widget)
      return { ...slot, widget: null }
    }
    return slot
  })
  for (const slot of row.slots.slice(nextCount)) {
    if (slot.widget) displaced.push(slot.widget)
  }

  const additions: Slot[] = []
  for (let i = kept.length; i < nextCount; i++) {
    const id = newSlotIds[i - kept.length]
    if (!id) throw new Error(`applyChangeRowLayout: missing slot id at index ${i}`)
    additions.push({ slotId: id, widget: null })
  }

  const nextRow: Row = { ...row, layout: nextLayout, slots: [...kept, ...additions] }
  const nextRows = layout.rows.map((r, i) => (i === rowIndex ? nextRow : r))
  return { layout: { ...layout, rows: nextRows }, displaced }
}

/**
 * Inserts a fresh empty row immediately after `afterRowId` (or at the top when
 * `afterRowId` is `null`). Slot ids are taken from `newSlotIds` so generation
 * stays out of the reducer.
 */
export function applyAddRow(
  layout: DashboardLayout,
  afterRowId: string | null,
  newRowLayout: RowLayout,
  newRowId: string,
  newSlotIds: string[]
): DashboardLayout {
  const slotCount = SLOT_COUNT_BY_LAYOUT[newRowLayout]
  if (newSlotIds.length < slotCount) {
    throw new Error(`applyAddRow: expected ${slotCount} slot ids, got ${newSlotIds.length}`)
  }
  const newRow: Row = {
    rowId: newRowId,
    layout: newRowLayout,
    slots: Array.from({ length: slotCount }, (_, i) => ({ slotId: newSlotIds[i]!, widget: null }))
  }
  if (afterRowId === null) {
    return { ...layout, rows: [newRow, ...layout.rows] }
  }
  const afterIndex = layout.rows.findIndex((r) => r.rowId === afterRowId)
  if (afterIndex < 0) return layout
  const next = [...layout.rows]
  next.splice(afterIndex + 1, 0, newRow)
  return { ...layout, rows: next }
}

export function applyRemoveRow(layout: DashboardLayout, rowId: string): DashboardLayout {
  const rowIndex = layout.rows.findIndex((r) => r.rowId === rowId)
  if (rowIndex < 0) return layout
  return { ...layout, rows: layout.rows.filter((r) => r.rowId !== rowId) }
}

/**
 * Strips any widget instance whose id is in `pinnedWidgetIds` from the layout.
 * Rows that become empty as a result are removed. Used to migrate legacy
 * persisted layouts that included widgets which have since been promoted to
 * page chrome. Returns the original reference when nothing changed so callers
 * can rely on reference equality.
 */
export function stripPinnedWidgets(layout: DashboardLayout, pinnedWidgetIds: ReadonlySet<string>): DashboardLayout {
  let mutated = false
  const nextRows: Row[] = []
  for (const row of layout.rows) {
    const nextSlots = row.slots.map((slot) => {
      if (slot.widget && pinnedWidgetIds.has(slot.widget.widgetId)) {
        mutated = true
        return { ...slot, widget: null }
      }
      return slot
    })
    // Drop a row entirely when every slot is empty AFTER stripping. We only do
    // this for rows that started fully occupied by pinned widgets — otherwise
    // a user with legitimate empty slots would lose their custom row.
    const allWerePinned = row.slots.every(
      (slot) => slot.widget !== null && pinnedWidgetIds.has(slot.widget.widgetId)
    )
    if (allWerePinned) {
      mutated = true
      continue
    }
    nextRows.push({ ...row, slots: nextSlots })
  }
  return mutated ? { ...layout, rows: nextRows } : layout
}

/**
 * Read-mode row shape: a row that contains exactly one full-bleed widget always
 * renders as a single full-width slot. `isFullBleed` is injected so the surface
 * decides which widget ids span the full grid width.
 */
export function getReadModeRow(row: Row, isFullBleed: (widgetId: string) => boolean): Row {
  const occupiedSlots = row.slots.filter((slot) => slot.widget !== null)
  if (occupiedSlots.length !== 1) return row
  const widgetId = occupiedSlots[0]!.widget!.widgetId
  if (!isFullBleed(widgetId)) return row
  if (row.layout === 'full' && row.slots.length === 1) return row
  return { ...row, layout: 'full', slots: [occupiedSlots[0]!] }
}

/**
 * Coerces rows that contain only a full-bleed widget into `layout: 'full'` with
 * a single slot. Repairs persisted layouts where a full-bleed widget was left
 * in a multi-column row (e.g. thirds), which would render at partial width.
 */
export function normalizeFullBleedRows(
  layout: DashboardLayout,
  isFullBleed: (widgetId: string) => boolean
): DashboardLayout {
  let mutated = false
  const nextRows = layout.rows.map((row) => {
    const occupiedSlots = row.slots.filter((slot) => slot.widget !== null)
    if (occupiedSlots.length !== 1) return row
    const widgetId = occupiedSlots[0]!.widget!.widgetId
    if (!isFullBleed(widgetId)) return row
    if (row.layout === 'full' && row.slots.length === 1) return row
    mutated = true
    return { ...row, layout: 'full' as const, slots: [occupiedSlots[0]!] }
  })
  return mutated ? { ...layout, rows: nextRows } : layout
}
